import { ParsedPromotionTemplate, PromotionTemplate } from "@shared/types";
import { PDFFont, PDFPage, rgb, StandardFonts } from "pdf-lib";
import { generateQRCodeAsImageData } from "../../qrCodeGenerator";
import { Dimensions, getScaledFontSize, getScaledSpacing } from "./dimensionUtils";
import { normalizeTextForWinAnsi, wrapText } from "./textUtils";

// Helper function to calculate promotion width
export const calculatePromotionWidth = (template: ParsedPromotionTemplate | undefined, dimensions: Dimensions): number => {
  if (!template?.width) {
    return dimensions.width;
  }

  if (template.width === "100%") {
    return Math.min(dimensions.width - dimensions.margin * 2, template?.maxWidth ?? 280);
  }

  return Math.min(Number(template.width), template?.maxWidth ?? 280);
};

// Helper function to get alignment based on image source type
export const getPromotionAlignment = (imageSourceType: string | undefined, defaultAlign: string | undefined): string => {
  return imageSourceType ? "left" : (defaultAlign ?? "center");
};

// Helper function to draw promotion border
export const drawPromotionBorder = (
  page: PDFPage,
  template: ParsedPromotionTemplate | undefined,
  dimensions: Dimensions,
  promotionWidth: number,
  startY: number,
  currentY: number
): void => {
  if (!template?.bordered) {
    return;
  }

  const contentHeight = currentY - startY;
  const startX = dimensions.width / 2 - promotionWidth / 2;
  page.drawRectangle({
    x: startX,
    y: dimensions.height - currentY,
    width: promotionWidth,
    height: contentHeight,
    borderColor: rgb(0, 0, 0),
    borderWidth: 1,
  });
};

export const drawPromotionTitle = (
  page: PDFPage,
  title: string,
  options: { x: number; y: number; width: number; fontSize: number; align: string },
  boldFont: PDFFont,
  dimensions: Dimensions
): number => {
  // Normalize text to handle Unicode characters that can't be encoded in WinAnsi
  const normalizedTitle = normalizeTextForWinAnsi(title);
  let textX = options.x;

  // Handle alignment
  if (options.align === "center") {
    const textWidth = boldFont.widthOfTextAtSize(normalizedTitle, options.fontSize);
    textX = options.x + (options.width - textWidth) / 2;
  } else if (options.align === "right") {
    const textWidth = boldFont.widthOfTextAtSize(normalizedTitle, options.fontSize);
    textX = options.x + options.width - textWidth;
  }
  // For "left" alignment, use options.x as is

  page.drawText(normalizedTitle, {
    x: textX,
    y: dimensions.height - options.y,
    font: boldFont,
    size: options.fontSize,
    color: rgb(0, 0, 0),
  });
  return options.y + options.fontSize + 5;
};

export const drawPromotionSubtext = (
  page: PDFPage,
  subtext: string,
  options: { x: number; y: number; width: number; fontSize: number; align: string },
  font: PDFFont,
  dimensions: Dimensions
): number => {
  // Normalize text to handle Unicode characters that can't be encoded in WinAnsi
  const normalizedSubtext = normalizeTextForWinAnsi(subtext);
  let textX = options.x;

  // Handle alignment
  if (options.align === "center") {
    const textWidth = font.widthOfTextAtSize(normalizedSubtext, options.fontSize);
    textX = options.x + (options.width - textWidth) / 2;
  } else if (options.align === "right") {
    const textWidth = font.widthOfTextAtSize(normalizedSubtext, options.fontSize);
    textX = options.x + options.width - textWidth;
  }
  // For "left" alignment, use options.x as is

  page.drawText(normalizedSubtext, {
    x: textX,
    y: dimensions.height - options.y,
    font,
    size: options.fontSize,
    color: rgb(0, 0, 0),
  });
  return options.y + options.fontSize + 5;
};

// Helper function to calculate image position based on alignment
export const calculateImagePosition = (
  imageConfig: { align?: string } | undefined,
  contentStartX: number,
  contentWidth: number,
  promotionWidth: number,
  imageWidth: number
): number => {
  if (imageConfig?.align === "center") {
    return contentStartX + contentWidth / 2 - imageWidth / 2;
  }
  if (imageConfig?.align === "right") {
    return promotionWidth - imageWidth;
  }
  return contentStartX;
};

// Helper function to process image data based on source type
export const processImageData = async (content: Record<string, string | undefined>): Promise<Uint8Array | undefined> => {
  const imageSourceType = content.imageSourceType;

  if (imageSourceType === "s3" && content.imageData) {
    // Decode base64 image data (remove data URL prefix if present)
    const base64Data = content.imageData.includes(",") ? (content.imageData.split(",")[1] ?? content.imageData) : content.imageData;

    const binaryString = atob(base64Data ?? "");
    const imageData = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      // NOSONAR : charCodeAt is correct for binary data conversion (0-255), codePointAt would be wrong for multi-byte Unicode
      imageData[i] = binaryString.charCodeAt(i); // NOSONAR
    }

    // Note: Image resizing optimization (if image exceeds 2x display size) would require
    // image decoding and manipulation, which adds complexity. Current implementation
    // focuses on skipping unnecessary processing for small images.

    return imageData;
  }

  if (imageSourceType === "qr" && content.qrData) {
    // Generate QR code (already optimized in qrCodeGenerator)
    return await generateQRCodeAsImageData(content.qrData);
  }

  return undefined;
};

// Helper function to draw image on PDF page
export interface ImageDrawingOptions {
  imageX: number;
  imageY: number;
  imageWidth: number;
  imageHeight: number;
  content: Record<string, string | undefined>;
  template: ParsedPromotionTemplate;
  dimensions: Dimensions;
}

export const drawPromotionImage = async (page: PDFPage, imageData: Uint8Array, options: ImageDrawingOptions): Promise<void> => {
  const { imageX, imageY, imageWidth, imageHeight, content, template, dimensions } = options;
  const imageFormat = content.imageFormat ?? "png";
  const embeddedImage = imageFormat === "png" ? await page.doc.embedPng(imageData) : await page.doc.embedJpg(imageData);

  // Align image with top of body text - add small offset for better alignment
  const bodyFontSize = template?.body?.fontSize ?? 8;
  const imageOffset = bodyFontSize * 0.5 + 3; // Half font size offset plus 3px for better alignment
  page.drawImage(embeddedImage, {
    x: imageX,
    y: dimensions.height - imageY - imageHeight + imageOffset,
    width: imageWidth,
    height: imageHeight,
  });
};

export interface DrawPromotionBodyOptions {
  page: PDFPage;
  content: Record<string, string | undefined>;
  template: ParsedPromotionTemplate;
  layout: {
    contentStartX: number;
    contentWidth: number;
    promotionWidth: number;
    currentY: number;
    padding: number;
    imageWidth: number;
    imageHeight: number;
  };
  imageX: number;
  imageY: number;
  hasImage: boolean;
  dimensions: Dimensions;
}

export const drawPromotionBody = async (options: DrawPromotionBodyOptions): Promise<number> => {
  const { page, content, template, layout, imageX, imageY, hasImage, dimensions } = options;
  const { contentStartX, contentWidth, currentY, padding, imageWidth, imageHeight } = layout;

  if (!content?.body) {
    return hasImage ? imageY + imageHeight : currentY;
  }

  const imageConfig = template?.image;
  const fontSize = getScaledFontSize(template?.body?.fontSize ?? 8, dimensions); // Smaller font for 3x4

  let bodyY = currentY;
  let bodyWidth: number;

  // For text-only promotions, use full width and left alignment
  let bodyX: number;
  if (hasImage) {
    bodyX =
      (imageConfig?.align === "right" && contentStartX) ||
      (imageConfig?.align === "center" && contentStartX) ||
      imageX + imageWidth + padding;
    bodyWidth = (imageConfig?.align === "center" && contentWidth) || contentWidth - (imageWidth + padding);
    bodyY = (imageConfig?.align === "center" && imageY + imageHeight + padding) || bodyY;
  } else {
    // Text-only promotion: use full width and left alignment
    bodyX = contentStartX;
    bodyWidth = contentWidth;
  }

  // Draw body text with proper wrapping
  const lines = wrapText(content.body, bodyWidth, fontSize);
  const bodyFont = await page.doc.embedFont(StandardFonts.Helvetica);
  for (const [index, line] of lines.entries()) {
    page.drawText(line, {
      x: bodyX,
      y: dimensions.height - (bodyY + index * (fontSize + 2)),
      font: bodyFont,
      size: fontSize,
      color: rgb(0, 0, 0),
    });
  }

  const bodyHeight = lines.length * (fontSize + 2);

  return hasImage
    ? (imageConfig?.align === "center" && bodyY + bodyHeight) || Math.max(imageY + imageHeight, imageY + bodyHeight)
    : currentY + bodyHeight;
};

export const drawPromotionImageAndBody = async (
  page: PDFPage,
  content: Record<string, string | undefined>,
  template: ParsedPromotionTemplate,
  layout: {
    contentStartX: number;
    contentWidth: number;
    promotionWidth: number;
    currentY: number;
    padding: number;
  },
  dimensions: Dimensions
): Promise<number> => {
  const { contentStartX, contentWidth, promotionWidth, currentY } = layout;
  const hasImage = !!content.imageSourceType && content.imageSourceType !== "text";
  const hasBody = !!content.body;

  if (!hasImage && !hasBody) {
    return currentY;
  }

  const imageConfig = template?.image;
  const imageWidth = imageConfig?.width ?? 35;
  const imageHeight = imageConfig?.height ?? 35;
  const imageX = calculateImagePosition(imageConfig, contentStartX, contentWidth, promotionWidth, imageWidth);
  const imageY = currentY;

  if (hasImage) {
    try {
      const imageData = await processImageData(content);
      if (imageData) {
        await drawPromotionImage(page, imageData, {
          imageX,
          imageY,
          imageWidth,
          imageHeight,
          content,
          template,
          dimensions,
        });
      }
    } catch (error) {
      console.warn("Error fetching promotion image", error);
    }
  }

  return await drawPromotionBody({
    page,
    content,
    template,
    layout: { ...layout, imageWidth, imageHeight },
    imageX,
    imageY,
    hasImage,
    dimensions,
  });
};

/**
 * Selects the appropriate localized content based on recipient locale with fallback to en_US
 * Handles case-insensitive locale matching (e.g., DE_DE, de_DE, De_De all match)
 */
const selectLocalizedContent = (
  localizedContent: Record<string, Record<string, string | undefined>> | undefined,
  recipientLocale: string
): Record<string, string | undefined> | undefined => {
  if (!localizedContent) {
    return undefined;
  }

  // Normalize recipient locale to lowercase and underscores (e.g., "de-DE" -> "de_de" or "DE_DE" -> "de_de")
  const normalizedRecipientLocale = recipientLocale.toLowerCase().replaceAll("-", "_");

  // Try exact match first (case-insensitive)
  for (const localeKey in localizedContent) {
    if (localeKey.toLowerCase() === normalizedRecipientLocale) {
      return localizedContent[localeKey];
    }
  }

  // Try matching just the language part (e.g., "de" from "de_DE")
  const languagePart = normalizedRecipientLocale.split("_")[0];
  for (const localeKey in localizedContent) {
    if (localeKey.toLowerCase().startsWith(languagePart + "_")) {
      return localizedContent[localeKey];
    }
  }
  // Also check for language-only keys (e.g., "de")
  for (const localeKey in localizedContent) {
    if (localeKey.toLowerCase() === languagePart) {
      return localizedContent[localeKey];
    }
  }

  // Fallback to en_US (case-insensitive)
  for (const localeKey in localizedContent) {
    if (localeKey.toLowerCase() === "en_us") {
      return localizedContent[localeKey];
    }
  }

  // Last resort: return first available locale
  const firstLocale = Object.keys(localizedContent)[0];
  return firstLocale ? localizedContent[firstLocale] : undefined;
};

export const drawPromotion = async (
  promotion: PromotionTemplate,
  page: PDFPage,
  dimensions: Dimensions,
  startY: number,
  font: PDFFont,
  boldFont: PDFFont,
  recipientLocale: string = "en_US"
): Promise<number> => {
  const { localizedContent } = promotion;
  const content = selectLocalizedContent(localizedContent, recipientLocale);

  if (!content) {
    return startY;
  }
  const template = promotion.template;
  const padding = template?.padding ?? 8;
  const promotionWidth = calculatePromotionWidth(template, dimensions);
  const contentWidth = promotionWidth - padding * 2;
  const contentStartX = dimensions.width / 2 - contentWidth / 2;

  // Add margin above promotion title
  let currentY = startY + padding + getScaledSpacing(10, 5, dimensions);

  if (content.title) {
    const titleAlign = getPromotionAlignment(content.imageSourceType, template?.title?.align);
    const titleFontSize = getScaledFontSize(template?.title?.fontSize ?? 12, dimensions);

    currentY = drawPromotionTitle(
      page,
      content.title,
      {
        x: contentStartX,
        y: currentY,
        width: contentWidth,
        fontSize: titleFontSize,
        align: titleAlign,
      },
      boldFont,
      dimensions
    );
  }

  // Handle image and body layout
  if (content.imageSourceType || content.body) {
    currentY = await drawPromotionImageAndBody(
      page,
      content,
      template,
      {
        contentStartX,
        contentWidth,
        promotionWidth,
        currentY,
        padding,
      },
      dimensions
    );
  }

  if (content.subtext) {
    const subtextAlign = getPromotionAlignment(content.imageSourceType, template?.subtext?.align);
    const subtextFontSize = getScaledFontSize(template?.subtext?.fontSize ?? 8, dimensions);

    currentY = drawPromotionSubtext(
      page,
      content.subtext,
      {
        x: contentStartX,
        y: currentY + (padding ?? 0),
        width: contentWidth,
        fontSize: subtextFontSize,
        align: subtextAlign,
      },
      font,
      dimensions
    );
  }

  drawPromotionBorder(page, template, dimensions, promotionWidth, startY, currentY);

  // Add margin below promotion before customer account name
  return currentY + padding + getScaledSpacing(15, 8, dimensions);
};
